import { useMemo } from "react";
import { ArrowRight, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import logoText from "@/assets/logo-text.webp";

/**
 * SplashScreen — first screen users see when opening TalkLot.
 * Per branding: text logo top-left, floating bubbles with connecting lines,
 * Continue button. Single-screen (no scroll), pure brand impression.
 */
const SplashScreen = ({ onFinished }: { onFinished: () => void }) => {
  // Pre-computed bubble positions so they don't reshuffle on re-render.
  const bubbles = useMemo(
    () => [
      { x: 12, y: 22, size: 64, delay: 0.0, dur: 6.5 },
      { x: 78, y: 18, size: 44, delay: 0.6, dur: 7.2 },
      { x: 28, y: 70, size: 52, delay: 0.3, dur: 6.8 },
      { x: 82, y: 62, size: 72, delay: 0.9, dur: 8.0 },
      { x: 55, y: 42, size: 36, delay: 0.4, dur: 6.0 },
      { x: 18, y: 48, size: 28, delay: 1.1, dur: 7.5 },
      { x: 68, y: 80, size: 40, delay: 0.2, dur: 7.0 },
      { x: 42, y: 12, size: 32, delay: 0.8, dur: 6.4 },
    ],
    []
  );

  // Connecting lines between selected bubble pairs.
  const lines: Array<[number, number]> = [
    [0, 4], [4, 1], [4, 3], [2, 5], [3, 6], [7, 1], [0, 2],
  ];

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden flex flex-col"
      style={{ background: "hsl(0 0% 4%)" }}
    >
      {/* Ambient brand glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-primary/[0.18] blur-[130px]" />
        <div className="absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full bg-accent/[0.18] blur-[130px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full bg-primary/[0.06] blur-[100px]" />
      </div>

      {/* Floating bubbles + connecting lines */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          <linearGradient id="lineGrad" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.55" />
            <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity="0.25" />
          </linearGradient>
        </defs>
        {lines.map(([a, b], i) => (
          <motion.line
            key={i}
            x1={bubbles[a].x}
            y1={bubbles[a].y}
            x2={bubbles[b].x}
            y2={bubbles[b].y}
            stroke="url(#lineGrad)"
            strokeWidth="0.15"
            strokeDasharray="0.6 0.6"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.7 }}
            transition={{ duration: 1.4, delay: 0.3 + i * 0.08, ease: "easeOut" }}
          />
        ))}
      </svg>

      {/* Bubbles layer */}
      <div className="absolute inset-0 pointer-events-none">
        {bubbles.map((b, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${b.x}%`,
              top: `${b.y}%`,
              width: b.size,
              height: b.size,
              transform: "translate(-50%, -50%)",
              background:
                "radial-gradient(circle at 30% 30%, hsl(var(--primary) / 0.55), hsl(var(--accent) / 0.25) 60%, transparent 75%)",
              boxShadow:
                "0 0 30px hsl(var(--primary) / 0.35), inset 0 0 12px hsl(var(--accent) / 0.25)",
              border: "1px solid hsl(var(--primary) / 0.35)",
              backdropFilter: "blur(2px)",
            }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{
              opacity: [0, 0.9, 0.7, 0.9],
              scale: [0.6, 1, 0.95, 1],
              y: [0, -10, 0, -8, 0],
            }}
            transition={{
              opacity: { duration: 1, delay: b.delay },
              scale: { duration: 1, delay: b.delay },
              y: {
                duration: b.dur,
                delay: b.delay,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          />
        ))}
      </div>

      {/* Top-left text logo (per branding rule) */}
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 pt-7 pl-5"
      >
        <img
          src={logoText}
          alt="TalkLot"
          width={140}
          height={36}
          decoding="async"
          className="h-9 w-auto object-contain"
          style={{ filter: "drop-shadow(0 0 18px hsl(var(--primary) / 0.6))" }}
        />
      </motion.div>

      {/* Center hero content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative mb-6"
        >
          <div className="absolute inset-0 rounded-full bg-primary/30 blur-2xl" />
          <div className="relative w-20 h-20 rounded-3xl gradient-premium flex items-center justify-center shadow-2xl">
            <MessageCircle className="w-10 h-10 text-primary-foreground" strokeWidth={2.4} />
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.35 }}
          className="text-[40px] leading-[1.05] font-display font-bold text-foreground mb-4 tracking-tight"
        >
          Say <span className="text-gradient-premium">hello</span>
          <br />to the world
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-muted-foreground text-[15px] leading-relaxed max-w-xs"
        >
          Real text chats with real people — anywhere, anytime.
        </motion.p>
      </div>

      {/* Continue CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.65 }}
        className="relative z-10 px-6 pb-9"
      >
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={onFinished}
          className="w-full py-4 rounded-2xl gradient-premium text-primary-foreground font-display font-bold text-base flex items-center justify-center gap-2"
          style={{
            boxShadow: "0 12px 36px -10px hsl(var(--primary) / 0.7)",
          }}
        >
          Continue <ArrowRight className="w-4 h-4" />
        </motion.button>
        <p className="text-center text-[11px] text-foreground/50 mt-3">
          By continuing, you agree to our Terms & Community Guidelines
        </p>
      </motion.div>
    </div>
  );
};

export default SplashScreen;